"use client";

import { useEffect, useRef, useMemo } from "react";
import { useStatsigClient } from "@statsig/react-bindings";
import { runStatsigSessionReplay } from "@statsig/session-replay";
import { runStatsigAutoCapture } from "@statsig/web-analytics";
import {
  useInitialContext,
  DefaultInitialContext,
  EXTERNAL_ACCOUNT_ID_COOKIE_NAME,
} from "@repo/sdk";
import useRoutePathname from "../hooks/use-route-pathname";
import useImpressionLogger from "../hooks/use-impression-logger";

/**
 * Syncs user details from initial context to Statsig, logs page views
 * and initializes session replay and autocapture in production.
 *
 * Note: Must be wrapped in Suspense - on client, useInitialContext throws (suspends)
 * when context is unavailable. On SSR, the hook returns null which we handle inline.
 */
export function StatsigObserver() {
  const initialContext = useInitialContext<DefaultInitialContext | null>();
  const { client } = useStatsigClient();

  // We have to have this useRef because runStatsigSessionReplay is not idempotent
  // so we have to track if we've already initialized it.
  const hasInitializedExtras = useRef(false);

  const user = useMemo(
    () => ({
      userID: initialContext?.account?.id,
      email: initialContext?.account?.email,
    }),
    [initialContext?.account?.id, initialContext?.account?.email]
  );

  // On SSR, useInitialContext returns null (doesn't throw). This check handles that case.
  // On client, useInitialContext throws (suspends) so this check is never reached - Suspense handles it.
  if (!initialContext) {
    return null;
  }

  // 1. Update Statsig user when email becomes available, preserving customIDs (stableID)
  useEffect(() => {
    if (user.userID && user.email && client) {
      const currentUser = client.getContext().user;
      client.updateUserSync({
        ...user,
        customIDs: currentUser?.customIDs,
      });
    }
  }, [user, client]);

  // 2. Set external-account-id cookie if not already present (backwards compat)
  useEffect(() => {
    if (!user.userID) return;
    const cookieExists = document.cookie
      .split("; ")
      .some((c) => c.startsWith(`${EXTERNAL_ACCOUNT_ID_COOKIE_NAME}=`));
    if (!cookieExists) {
      document.cookie = `${EXTERNAL_ACCOUNT_ID_COOKIE_NAME}=${user.userID}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
  }, [user.userID]);

  // 3. Log route impressions
  const { route } = useRoutePathname();
  useImpressionLogger(client, route, user);

  // 4. Initialize session replay and autocapture in production (once)
  useEffect(() => {
    if (!client || hasInitializedExtras.current) return;

    const isProduction =
      process.env.NEXT_PUBLIC_STATSIG_TIER === "production" ||
      process.env.VERCEL_TARGET_ENV === "production";

    if (isProduction) {
      runStatsigSessionReplay(client);
      runStatsigAutoCapture(client);
      hasInitializedExtras.current = true;
    }
  }, [client]);

  return null;
}

export default StatsigObserver;
